import { useState, useMemo } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { toast } from 'react-toastify';
import {
  Star, Megaphone, Plus, Trash2, ToggleLeft, ToggleRight,
  Edit2, X, Check, Settings, CalendarDays, DollarSign, Zap,
  Eye, EyeOff, AlertCircle, Package, BarChart2, TrendingUp, Clock,
} from 'lucide-react';
import { getProductImageUrl } from '../../utils/imageUrl';

// ── GraphQL ───────────────────────────────────────────────────────────────────

const GET_AD_RATES = gql`query GetAdRates { adRates { id adType pricePerDay description } }`;

const GET_SPONSORED_STATS = gql`
  query GetSponsoredStats {
    sponsoredStats {
      promotionId promotionName labNom
      sponsoredFrom sponsoredUntil pricePerDay
      daysTotal totalCost scanCount isActive daysRemaining
    }
  }
`;

const UPDATE_AD_RATE = gql`
  mutation UpdateAdRate($adType: AdType!, $pricePerDay: Float!, $description: String) {
    updateAdRate(adType: $adType, pricePerDay: $pricePerDay, description: $description) { id adType pricePerDay description }
  }
`;

// Stable query — only fields that exist on the old backend too
const GET_PROMOTIONS = gql`
  query GetPromotionsForAds {
    allPromotions {
      id
      name
      status
      startDate
      endDate
      commissionAmount
      laboratory { nom }
      products { id name image }
      isSponsored
      sponsoredFrom
      sponsoredUntil
      sponsoredPricePerDay
    }
  }
`;

const SET_SPONSORED = gql`
  mutation SetPromotionSponsored($id: ID!, $sponsoredFrom: DateTime, $sponsoredUntil: DateTime, $sponsoredPricePerDay: Float) {
    setPromotionSponsored(id: $id, sponsoredFrom: $sponsoredFrom, sponsoredUntil: $sponsoredUntil, sponsoredPricePerDay: $sponsoredPricePerDay) {
      id isSponsored sponsoredFrom sponsoredUntil sponsoredPricePerDay
    }
  }
`;
const REMOVE_SPONSORED = gql`
  mutation RemovePromotionSponsored($id: ID!) {
    removePromotionSponsored(id: $id) { id isSponsored }
  }
`;

const BANNER_FRAG = `id placement imageUrl linkUrl isActive isLive position startDate endDate pricePerDay totalCost promotionId promotion { id name products { id name image } } productId product { id name image price }`;
const GET_AD_BANNERS    = gql`query GetAdBanners { adBanners { ${BANNER_FRAG} } }`;
const CREATE_AD_BANNER  = gql`mutation CreateAdBanner($input: CreateAdBannerInput!) { createAdBanner(input: $input) { ${BANNER_FRAG} } }`;
const TOGGLE_AD_BANNER  = gql`mutation ToggleAdBanner($id: ID!, $isActive: Boolean!) { toggleAdBanner(id: $id, isActive: $isActive) { id isActive isLive } }`;
const UPDATE_AD_BANNER  = gql`mutation UpdateAdBanner($id: ID!, $input: CreateAdBannerInput!) { updateAdBanner(id: $id, input: $input) { ${BANNER_FRAG} } }`;
const REMOVE_AD_BANNER  = gql`mutation RemoveAdBanner($id: ID!) { removeAdBanner(id: $id) }`;
const REORDER_AD_BANNERS = gql`mutation ReorderAdBanners($items: [BannerPositionInput!]!) { reorderAdBanners(items: $items) }`;

// ── Constants ─────────────────────────────────────────────────────────────────

const AD_TYPE_META = {
  SPONSORED_PRODUCT: { label: 'Produit Sponsorisé', icon: Star,     color: 'amber',  desc: 'Promotion en tête de liste "Toutes" avec badge doré' },
  BANNER_HOME:       { label: 'Bannière Accueil',   icon: Megaphone, color: 'blue',   desc: 'Bannière pleine largeur avant les promotions' },
  BANNER_SCANNER:    { label: 'Bannière Scanner',   icon: Zap,       color: 'purple', desc: 'Bannière dans la section produits récents du scanner' },
};

const PLACEMENT_META = {
  HOME:    { label: "Page d'accueil", adType: 'BANNER_HOME' },
  SCANNER: { label: 'Scanner',        adType: 'BANNER_SCANNER' },
};

const CLR = {
  amber:  { bg: 'bg-amber-50',  border: 'border-amber-200', text: 'text-amber-700',  badge: 'bg-amber-100 text-amber-700',   icon: 'text-amber-500' },
  blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',  text: 'text-blue-700',   badge: 'bg-blue-100 text-blue-700',     icon: 'text-blue-500' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200',text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700', icon: 'text-purple-500' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const API_REST = (process.env.REACT_APP_API_URL || 'http://localhost:3001/graphql').replace('/graphql', '');

// Resolve a banner imageUrl (may be relative path like "banners/xxx.jpg" or absolute URL)
const getBannerImageUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//.test(url) || url.startsWith('data:')) return url;
  return `${API_REST}/api/files/banner-images/${url.replace(/^banners\//, '')}`;
};

const fmt        = (n)    => Number(n || 0).toFixed(2);
const fmtDate    = (d)    => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const toDateInput= (d)    => d ? new Date(d).toISOString().split('T')[0] : '';
const today      = ()     => new Date().toISOString().split('T')[0];
const daysBetween= (a, b) => { if (!a || !b) return 0; return Math.max(1, Math.ceil((new Date(b) - new Date(a)) / 86400000)); };

// Build flat list of {promo, product} pairs from active promotions (only IN_PROGRESS)
const flattenProducts = (promotions) => {
  const map = new Map();
  const now = new Date();
  // Sponsored first so they win deduplication
  const sorted = [...promotions]
    .filter(p => p.status === 'IN_PROGRESS' && new Date(p.endDate) >= now)
    .sort((a, b) => (b.isSponsored ? 1 : 0) - (a.isSponsored ? 1 : 0));
  sorted.forEach(promo => {
    (promo.products || []).forEach(product => {
      if (!map.has(product.id)) map.set(product.id, { promo, product });
    });
  });
  return Array.from(map.values());
};


// ── PricingCard ───────────────────────────────────────────────────────────────

const PricingCard = ({ rate, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [price, setPrice]     = useState(rate.pricePerDay);
  const [desc, setDesc]       = useState(rate.description || '');
  const meta   = AD_TYPE_META[rate.adType] || {};
  const colors = CLR[meta.color] || CLR.amber;
  const Icon   = meta.icon || Star;

  const handleSave = async () => { await onSave(rate.adType, parseFloat(price) || 0, desc); setEditing(false); };

  return (
    <div className={`rounded-xl border ${colors.border} ${colors.bg} p-4`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={15} className={colors.icon} />
          <span className={`text-sm font-bold ${colors.text}`}>{meta.label}</span>
        </div>
        {!editing && <button onClick={() => setEditing(true)} className="p-1 rounded hover:bg-white/60 text-gray-400"><Edit2 size={12} /></button>}
      </div>
      <p className="text-xs text-gray-500 mb-3">{meta.desc}</p>
      {editing ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-24 shrink-0">Prix/jour (DH)</span>
            <input type="number" min={0} step={5} value={price} onChange={e => setPrice(e.target.value)}
              className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-24 shrink-0">Description</span>
            <input type="text" value={desc} onChange={e => setDesc(e.target.value)}
              className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={handleSave} className="flex items-center gap-1 px-3 py-1.5 bg-green-700 text-white text-xs font-semibold rounded-lg hover:bg-green-800"><Check size={12} /> Sauvegarder</button>
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 bg-gray-200 text-gray-600 text-xs font-semibold rounded-lg hover:bg-gray-300">Annuler</button>
          </div>
        </div>
      ) : (
        <div className={`text-2xl font-black ${colors.text}`}>{fmt(rate.pricePerDay)} <span className="text-base font-semibold">DH/jour</span></div>
      )}
    </div>
  );
};

// ── SponsorForm ───────────────────────────────────────────────────────────────

const SponsorForm = ({ item, ratePerDay, onSave, onCancel }) => {
  const t      = today();
  const [from, setFrom] = useState(toDateInput(item.promo.sponsoredFrom) || t);
  const [until, setUntil] = useState(toDateInput(item.promo.sponsoredUntil) || t);
  const [ppd, setPpd]   = useState(item.promo.sponsoredPricePerDay ?? ratePerDay ?? 50);

  const days  = daysBetween(from, until);
  const total = days * parseFloat(ppd || 0);

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3 mt-2 ml-12">
      <p className="text-xs font-bold text-amber-800">Définir la période de sponsoring</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Date de début</label>
          <input type="date" value={from} min={t} onChange={e => setFrom(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Date de fin</label>
          <input type="date" value={until} min={from || t} onChange={e => setUntil(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white" />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <label className="block text-xs text-gray-500 mb-1">Prix par jour (DH)</label>
          <input type="number" min={0} step={5} value={ppd} onChange={e => setPpd(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white" />
        </div>
        <div className="flex-1 bg-white border border-amber-200 rounded-xl px-3 py-2 text-center">
          <div className="text-xs text-gray-500">{days} jour{days !== 1 ? 's' : ''}</div>
          <div className="text-xl font-black text-amber-700">{fmt(total)} DH</div>
          <div className="text-xs text-gray-400">coût total</div>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={() => { if (!from || !until) { toast.error('Dates requises'); return; } onSave(from, until, parseFloat(ppd) || 0); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700">
          <Star size={13} /> Sponsoriser
        </button>
        <button onClick={onCancel} className="px-4 py-2 bg-gray-200 text-gray-600 text-sm font-semibold rounded-lg hover:bg-gray-300">Annuler</button>
      </div>
    </div>
  );
};

// ── BannerForm ────────────────────────────────────────────────────────────────

const BannerForm = ({ onSave, onCancel, initial = null, rates = [], allProducts = [] }) => {
  const t = today();
  const rateFor = (pl) => rates.find(r => r.adType === PLACEMENT_META[pl]?.adType)?.pricePerDay || 0;
  const [form, setForm] = useState({
    placement:  initial?.placement  || 'HOME',
    imageUrl:   initial?.imageUrl   || '',
    linkUrl:    initial?.linkUrl    || '',
    productId:  initial?.productId  || '',
    position:   initial?.position   ?? 0,
    startDate:  toDateInput(initial?.startDate) || t,
    endDate:    toDateInput(initial?.endDate)   || t,
    pricePerDay: initial?.pricePerDay ?? rateFor(initial?.placement || 'HOME'),
  });
  const [imageMode, setImageMode] = useState('url');
  const [uploading, setUploading] = useState(false);

  const days  = daysBetween(form.startDate, form.endDate);
  const total = days * parseFloat(form.pricePerDay || 0);
  const onPl  = (p) => setForm(f => ({ ...f, placement: p, pricePerDay: rateFor(p) }));

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await fetch(`${API_REST}/api/files/upload-banner-image`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Upload failed');
      const json = await res.json();
      setForm(f => ({ ...f, imageUrl: json.path }));
    } catch {
      toast.error("Erreur lors de l'upload de l'image");
    } finally {
      setUploading(false);
    }
  };

  const selectedProduct = allProducts.find(p => p.id === form.productId);

  return (
    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-3">
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">Emplacement</label>
        <select value={form.placement} onChange={e => onPl(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
          <option value="HOME">Page d'accueil</option>
          <option value="SCANNER">Scanner</option>
        </select>
      </div>

      {/* Image source toggle */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-2">Image de la bannière *</label>
        <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit mb-3">
          <button type="button" onClick={() => setImageMode('url')}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${imageMode === 'url' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            URL
          </button>
          <button type="button" onClick={() => setImageMode('upload')}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${imageMode === 'upload' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            Upload fichier
          </button>
        </div>
        {imageMode === 'url' ? (
          <input type="text" placeholder="https://..." value={form.imageUrl} onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white" />
        ) : (
          <div className="flex items-center gap-3">
            <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-gray-300 bg-white text-sm text-gray-500 cursor-pointer hover:border-blue-400 hover:text-blue-600 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
              {uploading ? <><span className="animate-spin inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" /> Envoi...</> : <><Plus size={14} /> Choisir une image</>}
              <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={handleFileChange} disabled={uploading} />
            </label>
            {form.imageUrl && <span className="text-xs text-green-600 font-semibold">✓ Image uploadée</span>}
          </div>
        )}
        {form.imageUrl && (
          <img src={getBannerImageUrl(form.imageUrl)} alt="preview" className="mt-2 h-20 rounded-lg object-cover border border-gray-200" onError={e => { e.target.style.display = 'none'; }} />
        )}
      </div>

      {/* Produit lié */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">Produit lié (recommandé)</label>
        <p className="text-xs text-gray-400 mb-1">Appuyer sur la bannière ouvre la page détail de ce produit dans l'app.</p>
        <select value={form.productId} onChange={e => setForm(f => ({ ...f, productId: e.target.value }))}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">— Aucun produit lié —</option>
          {allProducts.map(p => (
            <option key={p.id} value={p.id}>{p.name}{p.laboratory?.nom ? ` · ${p.laboratory.nom}` : ''}</option>
          ))}
        </select>
        {selectedProduct && (
          <div className="flex items-center gap-2 mt-1.5 p-2 bg-white border border-gray-100 rounded-lg">
            {selectedProduct.image && <img src={getProductImageUrl(selectedProduct.image)} alt="" className="w-8 h-8 rounded object-cover border border-gray-100" onError={e => { e.target.style.display = 'none'; }} />}
            <span className="text-xs font-semibold text-gray-700">{selectedProduct.name}</span>
            <span className="text-xs text-gray-400 ml-auto">{selectedProduct.price?.toFixed(2)} DH</span>
          </div>
        )}
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">Lien au clic (optionnel)</label>
        <p className="text-xs text-gray-400 mb-1">Si aucun produit lié, l'utilisateur est redirigé vers cette URL externe.</p>
        <input type="text" placeholder="https://..." value={form.linkUrl} onChange={e => setForm(f => ({ ...f, linkUrl: e.target.value }))}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Date de début *</label>
          <input type="date" value={form.startDate} min={t} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Date de fin *</label>
          <input type="date" value={form.endDate} min={form.startDate || t} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white" />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1">
          <label className="block text-xs font-semibold text-gray-500 mb-1">Prix par jour (DH)</label>
          <input type="number" min={0} step={5} value={form.pricePerDay} onChange={e => setForm(f => ({ ...f, pricePerDay: parseFloat(e.target.value) || 0 }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white" />
        </div>
        <div className="flex-1 bg-white border border-green-200 rounded-xl px-3 py-2 text-center">
          <div className="text-xs text-gray-500">{days} jour{days !== 1 ? 's' : ''}</div>
          <div className="text-xl font-black text-green-700">{fmt(total)} DH</div>
          <div className="text-xs text-gray-400">coût total</div>
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={() => {
          if (!form.imageUrl.trim()) { toast.error("L'URL de l'image est requise"); return; }
          if (!form.startDate || !form.endDate) { toast.error('Les dates sont requises'); return; }
          onSave({ ...form, productId: form.productId || null, promotionId: null });
        }}
          disabled={uploading}
          className="flex items-center gap-1.5 px-4 py-2 bg-green-700 text-white text-sm font-semibold rounded-lg hover:bg-green-800 disabled:opacity-50">
          <Check size={14} /> Enregistrer
        </button>
        <button onClick={onCancel} className="flex items-center gap-1.5 px-4 py-2 bg-gray-200 text-gray-600 text-sm font-semibold rounded-lg hover:bg-gray-300">
          <X size={14} /> Annuler
        </button>
      </div>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────

const Advertising = () => {
  const [tab, setTab]             = useState('sponsored');
  const [bannerPlacement, setBannerPlacement] = useState('HOME');
  const [showBannerForm, setShowBannerForm] = useState(false);
  const [editingBanner, setEditingBanner]   = useState(null);
  const [sponsoringKey, setSponsoringKey]   = useState(null); // "promoId-productId"
  const [search, setSearch]       = useState('');
  const [filter, setFilter]       = useState('all');
  const [dragId, setDragId]       = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [calYear, setCalYear]     = useState(new Date().getFullYear());
  const [calMonth, setCalMonth]   = useState(new Date().getMonth());
  const [calDay, setCalDay]       = useState(null);

  // ── Queries ────────────────────────────────────────────────────────────────

  const {
    data: ratesData, loading: ratesLoading, error: ratesError, refetch: refetchRates,
  } = useQuery(GET_AD_RATES, { fetchPolicy: 'cache-and-network' });

  const {
    data: promoData, loading: promoLoading, error: promoError, refetch: refetchPromos,
  } = useQuery(GET_PROMOTIONS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all', // don't throw if new fields don't exist yet
  });

  const {
    data: bannerData, loading: bannerLoading, refetch: refetchBanners,
  } = useQuery(GET_AD_BANNERS, { fetchPolicy: 'cache-and-network' });

  const {
    data: statsData, loading: statsLoading, refetch: refetchStats,
  } = useQuery(GET_SPONSORED_STATS, { fetchPolicy: 'cache-and-network' });

  // ── Mutations ──────────────────────────────────────────────────────────────

  const [updateAdRate]    = useMutation(UPDATE_AD_RATE);
  const [setSponsored]    = useMutation(SET_SPONSORED);
  const [removeSponsored] = useMutation(REMOVE_SPONSORED);
  const [createBanner]    = useMutation(CREATE_AD_BANNER);
  const [toggleBanner]    = useMutation(TOGGLE_AD_BANNER);
  const [updateBanner]    = useMutation(UPDATE_AD_BANNER);
  const [removeBanner]    = useMutation(REMOVE_AD_BANNER);
  const [reorderBanners]  = useMutation(REORDER_AD_BANNERS);

  // ── Derived data ──────────────────────────────────────────────────────────

  const rates      = ratesData?.adRates || [];
  const promotions = promoData?.allPromotions || [];
  const banners    = bannerData?.adBanners || [];
  const sponsoredStatsList = statsData?.sponsoredStats || [];

  const sponsoredRate = rates.find(r => r.adType === 'SPONSORED_PRODUCT')?.pricePerDay || 50;

  // Stats aggregates
  const statsActive    = sponsoredStatsList.filter(s => s.isActive).length;
  const statsTotal     = sponsoredStatsList.length;
  const statsTotalCost = sponsoredStatsList.reduce((acc, s) => acc + (s.totalCost || 0), 0);
  const statsTotalScans= sponsoredStatsList.reduce((acc, s) => acc + (s.scanCount || 0), 0);

  // Flat {promo, product} pairs from active promotions — prefer sponsored promos
  const allProductItems = useMemo(() => flattenProducts(promotions), [promotions]);

  // All unique products across all promotions (for banner product picker)
  const allBannerProducts = useMemo(() => {
    const map = new Map();
    promotions.forEach(promo => {
      (promo.products || []).forEach(product => {
        if (!map.has(product.id)) map.set(product.id, { ...product });
      });
    });
    return Array.from(map.values());
  }, [promotions]);

  // Calendar events: sponsored promos + banners
  const calEvents = useMemo(() => {
    const evts = [];
    promotions.filter(p => p.isSponsored && p.sponsoredFrom).forEach(p => {
      evts.push({
        id: `sp-${p.id}`,
        type: 'sponsored',
        label: p.name,
        sub: p.laboratory?.nom,
        from: new Date(p.sponsoredFrom),
        to: p.sponsoredUntil ? new Date(p.sponsoredUntil) : new Date(p.sponsoredFrom),
        dotColor: '#F59E0B',
        badgeCls: 'bg-amber-100 text-amber-800 border-amber-200',
        Icon: Star,
        extra: `${fmt(p.sponsoredPricePerDay || 0)} DH/j`,
      });
    });
    banners.filter(b => b.startDate).forEach(b => {
      const isHome = b.placement === 'HOME';
      evts.push({
        id: `bn-${b.id}`,
        type: 'banner',
        label: b.product?.name || (isHome ? 'Bannière Accueil' : 'Bannière Scanner'),
        sub: isHome ? "Page d'accueil" : 'Scanner',
        from: new Date(b.startDate),
        to: b.endDate ? new Date(b.endDate) : new Date(b.startDate),
        dotColor: isHome ? '#3B82F6' : '#8B5CF6',
        badgeCls: isHome ? 'bg-blue-100 text-blue-800 border-blue-200' : 'bg-purple-100 text-purple-800 border-purple-200',
        Icon: isHome ? Megaphone : Zap,
        extra: `${fmt(b.pricePerDay)} DH/j · ${fmt(b.totalCost)} DH total`,
      });
    });
    return evts;
  }, [promotions, banners]);

  const calDayEvents = useMemo(() => {
    if (!calDay) return [];
    const d = new Date(calYear, calMonth, calDay);
    const dEnd = new Date(calYear, calMonth, calDay, 23, 59, 59, 999);
    return calEvents.filter(ev => ev.from <= dEnd && ev.to >= d);
  }, [calDay, calYear, calMonth, calEvents]);

  const calDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const calFirstOffset = (y, m) => (new Date(y, m, 1).getDay() + 6) % 7; // Mon-start

  const getDotsForDay = (d) => {
    const day = new Date(calYear, calMonth, d);
    const dayEnd = new Date(calYear, calMonth, d, 23, 59, 59, 999);
    return calEvents.filter(ev => ev.from <= dayEnd && ev.to >= day);
  };

  const filteredItems = useMemo(() => {
    let list = allProductItems.filter(({ product, promo }) =>
      product.name?.toLowerCase().includes(search.toLowerCase()) ||
      promo.name?.toLowerCase().includes(search.toLowerCase()) ||
      promo.laboratory?.nom?.toLowerCase().includes(search.toLowerCase())
    );
    if (filter === 'active')   list = list.filter(({ promo }) => promo.isSponsored);
    if (filter === 'inactive') list = list.filter(({ promo }) => !promo.isSponsored);
    // Sponsored items first
    return [...list].sort((a, b) => (b.promo.isSponsored ? 1 : 0) - (a.promo.isSponsored ? 1 : 0));
  }, [allProductItems, search, filter]);

  const activeSponsored = promotions.filter(p => p.isSponsored);
  const activeBanners   = banners.filter(b => b.isLive);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSaveRate = async (adType, pricePerDay, description) => {
    try { await updateAdRate({ variables: { adType, pricePerDay, description } }); await refetchRates(); toast.success('Tarif mis à jour'); }
    catch { toast.error('Erreur lors de la mise à jour du tarif'); }
  };

  const handleSponsor = async (promoId, from, until, ppd) => {
    try {
      // Set until to end-of-day so same-day sponsorships work correctly
      const untilEod = new Date(until);
      untilEod.setHours(23, 59, 59, 999);
      await setSponsored({ variables: { id: promoId, sponsoredFrom: new Date(from).toISOString(), sponsoredUntil: untilEod.toISOString(), sponsoredPricePerDay: ppd } });
      await refetchPromos();
      await refetchStats();
      setSponsoringKey(null);
      toast.success('Produit sponsorisé');
    } catch (e) { console.error(e); toast.error('Erreur lors du sponsoring'); }
  };

  const handleRemoveSponsored = async (promoId) => {
    try { await removeSponsored({ variables: { id: promoId } }); await refetchPromos(); await refetchStats(); toast.success('Sponsoring retiré'); }
    catch { toast.error('Erreur'); }
  };

  const toEod = (dateStr) => { const d = new Date(dateStr); d.setHours(23, 59, 59, 999); return d.toISOString(); };

  const handleCreateBanner = async (form) => {
    try {
      await createBanner({ variables: { input: { ...form, startDate: new Date(form.startDate).toISOString(), endDate: toEod(form.endDate) } } });
      await refetchBanners(); setShowBannerForm(false); toast.success('Bannière créée');
    } catch { toast.error('Erreur lors de la création'); }
  };

  const handleUpdateBanner = async (form) => {
    try {
      await updateBanner({ variables: { id: editingBanner.id, input: { ...form, startDate: new Date(form.startDate).toISOString(), endDate: toEod(form.endDate) } } });
      await refetchBanners(); setEditingBanner(null); toast.success('Bannière mise à jour');
    } catch { toast.error('Erreur'); }
  };

  const handleToggleBanner   = async (banner) => { try { await toggleBanner({ variables: { id: banner.id, isActive: !banner.isActive } }); await refetchBanners(); } catch { toast.error('Erreur'); } };
  const handleRemoveBanner   = async (id) => { if (!window.confirm('Supprimer ?')) return; try { await removeBanner({ variables: { id } }); await refetchBanners(); toast.success('Supprimée'); } catch { toast.error('Erreur'); } };

  const statusBadge = (status) => {
    const m = { IN_PROGRESS: ['bg-green-100 text-green-700', 'En cours'], DRAFT: ['bg-gray-100 text-gray-600', 'Brouillon'], CLOSED: ['bg-red-100 text-red-600', 'Clôturée'] };
    const [cls, label] = m[status] || ['bg-gray-100 text-gray-600', status];
    return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Publicités</h1>
          <p className="text-sm text-gray-500 mt-1">Bannières et produits sponsorisés dans l'app mobile.</p>
        </div>
        <div className="flex gap-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-center">
            <div className="text-xs text-amber-600 font-semibold">Sponsorisés actifs</div>
            <div className="text-xl font-black text-amber-700">{activeSponsored.length}</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-center">
            <div className="text-xs text-blue-600 font-semibold">Bannières actives</div>
            <div className="text-xl font-black text-blue-700">{activeBanners.length}</div>
          </div>
        </div>
      </div>

      {/* ── Pricing strategy ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Settings size={15} className="text-gray-400" />
          <h2 className="text-sm font-bold text-gray-700">Stratégie tarifaire</h2>
          <span className="text-xs text-gray-400">· Modifiable à tout moment</span>
        </div>

        {ratesLoading && rates.length === 0 ? (
          <div className="flex gap-4">
            {[1,2,3].map(i => <div key={i} className="flex-1 h-24 bg-gray-100 rounded-xl animate-pulse" />)}
          </div>
        ) : ratesError && rates.length === 0 ? (
          <div className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-xl p-4 text-sm text-orange-700">
            <AlertCircle size={16} className="shrink-0" />
            <span>La fonctionnalité de tarification nécessite une mise à jour du backend. Appliquez les migrations et reconstruisez le serveur.</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {rates.map(rate => <PricingCard key={rate.id} rate={rate} onSave={handleSaveRate} />)}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <button onClick={() => setTab('sponsored')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${tab === 'sponsored' ? 'border-amber-500 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          <Star size={15} /> Produits sponsorisés
          {activeSponsored.length > 0 && <span className="bg-amber-100 text-amber-700 text-xs font-bold px-1.5 py-0.5 rounded-full">{activeSponsored.length}</span>}
        </button>
        <button onClick={() => setTab('banners')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${tab === 'banners' ? 'border-blue-500 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          <Megaphone size={15} /> Bannières publicitaires
          {activeBanners.length > 0 && <span className="bg-blue-100 text-blue-700 text-xs font-bold px-1.5 py-0.5 rounded-full">{activeBanners.length}</span>}
        </button>
        <button onClick={() => setTab('calendar')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${tab === 'calendar' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          <CalendarDays size={15} /> Calendrier
        </button>
      </div>

      {/* ── Sponsored tab ── */}
      {tab === 'sponsored' && (
        <div>
          {promoError && (
            <div className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4 text-sm text-orange-700">
              <AlertCircle size={16} className="shrink-0" />
              <span>Impossible de charger les promotions. Vérifiez que le backend est à jour (migrations + rebuild).</span>
            </div>
          )}

          {/* ── Stats row ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {[
              { label: 'Actifs', val: statsActive, icon: Star, color: 'amber' },
              { label: 'Total historique', val: statsTotal, icon: Clock, color: 'gray' },
              { label: 'Scans pendant sponso', val: statsTotalScans, icon: BarChart2, color: 'blue' },
              { label: 'Investissement total', val: `${fmt(statsTotalCost)} DH`, icon: TrendingUp, color: 'green' },
            ].map(({ label, val, icon: Icon, color }) => {
              const cls = { amber: 'bg-amber-50 border-amber-200 text-amber-700', gray: 'bg-gray-50 border-gray-200 text-gray-600', blue: 'bg-blue-50 border-blue-200 text-blue-700', green: 'bg-green-50 border-green-200 text-green-700' }[color];
              return (
                <div key={label} className={`rounded-xl border p-4 ${cls}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={14} />
                    <span className="text-xs font-semibold">{label}</span>
                  </div>
                  <div className="text-2xl font-black">{val}</div>
                </div>
              );
            })}
          </div>

          {/* ── Sponsored history table ── */}
          {sponsoredStatsList.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-5 overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100">
                <BarChart2 size={14} className="text-gray-400" />
                <h3 className="text-sm font-bold text-gray-700">Historique des sponsorisations</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500 font-semibold">
                      <th className="px-4 py-2 text-left">Promotion</th>
                      <th className="px-4 py-2 text-left">Labo</th>
                      <th className="px-4 py-2 text-center">Période</th>
                      <th className="px-4 py-2 text-right">Durée</th>
                      <th className="px-4 py-2 text-right">Coût</th>
                      <th className="px-4 py-2 text-right">Scans</th>
                      <th className="px-4 py-2 text-center">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sponsoredStatsList.map(s => (
                      <tr key={s.promotionId} className="border-t border-gray-50 hover:bg-gray-50/50">
                        <td className="px-4 py-2.5 font-semibold text-gray-800 max-w-[160px] truncate">{s.promotionName}</td>
                        <td className="px-4 py-2.5 text-gray-500 max-w-[100px] truncate">{s.labNom || '—'}</td>
                        <td className="px-4 py-2.5 text-center text-gray-500">
                          {fmtDate(s.sponsoredFrom)}{s.sponsoredUntil ? ` → ${fmtDate(s.sponsoredUntil)}` : ''}
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold">{s.daysTotal}j</td>
                        <td className="px-4 py-2.5 text-right font-bold text-amber-700">{fmt(s.totalCost)} DH</td>
                        <td className="px-4 py-2.5 text-right font-bold text-blue-700">{s.scanCount}</td>
                        <td className="px-4 py-2.5 text-center">
                          {s.isActive
                            ? <span className="bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded-full">Actif · {s.daysRemaining}j restants</span>
                            : <span className="bg-gray-100 text-gray-500 font-semibold px-2 py-0.5 rounded-full">Terminé</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Search + filter */}
          <div className="flex gap-3 mb-4">
            <input type="text" placeholder="Rechercher un produit, une promotion, un laboratoire..."
              className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400"
              value={search} onChange={e => setSearch(e.target.value)} />
            <select value={filter} onChange={e => setFilter(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-600">
              <option value="all">Tous les produits</option>
              <option value="active">Sponsorisés</option>
              <option value="inactive">Non sponsorisés</option>
            </select>
          </div>

          {promoLoading ? (
            <div className="space-y-2">{[1,2,3,4,5].map(i => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}</div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl">
              <Package size={36} className="text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400 text-sm font-medium">Aucun produit en promotion active</p>
              <p className="text-gray-300 text-xs mt-1">Seuls les produits des promotions "En cours" apparaissent ici.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredItems.map(({ promo, product }) => {
                const key = `${promo.id}-${product.id}`;
                const uri = getProductImageUrl(product.image);
                const isSponsoring = sponsoringKey === key;

                return (
                  <div key={key}>
                    <div className={`flex items-center gap-3 bg-white rounded-xl px-4 py-3 border transition-all ${promo.isSponsored ? 'border-amber-300 shadow-sm' : 'border-gray-100 hover:border-gray-200'}`}>
                      {/* Product image */}
                      {uri ? (
                        <img src={uri} alt={product.name} className="w-10 h-10 rounded-lg object-cover border border-gray-100 shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                          <Package size={16} className="text-gray-300" />
                        </div>
                      )}

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm text-gray-900 truncate">{product.name}</span>
                          {promo.isSponsored && (
                            <span className="flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full shrink-0">
                              <Star size={9} fill="currentColor" /> Sponsorisé
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5 truncate">
                          {promo.name} · {promo.laboratory?.nom}
                          {promo.isSponsored && promo.sponsoredFrom && (
                            <> · <span className="text-amber-600 font-semibold">{fmtDate(promo.sponsoredFrom)} → {fmtDate(promo.sponsoredUntil)}</span>
                              {' · '}{fmt(promo.sponsoredPricePerDay)} DH/j · <span className="font-semibold">{fmt(daysBetween(promo.sponsoredFrom, promo.sponsoredUntil) * (promo.sponsoredPricePerDay || 0))} DH total</span>
                            </>
                          )}
                        </p>
                      </div>

                      {/* Commission badge */}
                      <div className="text-xs font-bold text-green-700 bg-green-100 px-2 py-1 rounded-lg shrink-0">
                        +{Number(promo.commissionAmount || 0).toFixed(0)} DH
                      </div>

                      {/* Status + action */}
                      {statusBadge(promo.status)}

                      {promo.isSponsored ? (
                        <div className="flex gap-1 shrink-0">
                          <button onClick={() => setSponsoringKey(isSponsoring ? null : key)}
                            className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-500"><Edit2 size={13} /></button>
                          <button onClick={() => handleRemoveSponsored(promo.id)}
                            className="flex items-center gap-1 px-2 py-1.5 text-xs font-semibold bg-red-50 text-red-500 rounded-lg hover:bg-red-100">
                            <X size={12} /> Retirer
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => setSponsoringKey(isSponsoring ? null : key)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700 hover:bg-amber-200 shrink-0">
                          <Star size={12} /> Sponsoriser
                        </button>
                      )}
                    </div>

                    {/* Inline date/price form */}
                    {isSponsoring && (
                      <SponsorForm
                        item={{ promo, product }}
                        ratePerDay={sponsoredRate}
                        onSave={(from, until, ppd) => handleSponsor(promo.id, from, until, ppd)}
                        onCancel={() => setSponsoringKey(null)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Banners tab ── */}
      {tab === 'banners' && (
        <div>
          {/* Sub-tabs: HOME / SCANNER */}
          <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl w-fit">
            {['HOME', 'SCANNER'].map(pl => (
              <button key={pl} onClick={() => { setBannerPlacement(pl); setShowBannerForm(false); setEditingBanner(null); }}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${bannerPlacement === pl ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {pl === 'HOME' ? <Eye size={13} /> : <Zap size={13} />}
                {PLACEMENT_META[pl].label}
                {(() => { const n = banners.filter(b => b.placement === pl && b.isLive).length; return n > 0 ? <span className="bg-blue-100 text-blue-700 text-xs font-bold px-1.5 py-0.5 rounded-full">{n}</span> : null; })()}
              </button>
            ))}
          </div>

          {/* Add button + form */}
          {!showBannerForm && !editingBanner && (
            <button onClick={() => setShowBannerForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 mb-5">
              <Plus size={15} /> Nouvelle bannière — {PLACEMENT_META[bannerPlacement].label}
            </button>
          )}
          {showBannerForm && (
            <div className="mb-5">
              <BannerForm
                initial={{ placement: bannerPlacement }}
                rates={rates} allProducts={allBannerProducts}
                onSave={handleCreateBanner} onCancel={() => setShowBannerForm(false)} />
            </div>
          )}

          {bannerLoading ? (
            <div className="flex items-center justify-center py-20 text-gray-400">Chargement...</div>
          ) : (() => {
            const now = new Date();
            const group = banners
              .filter(b => b.placement === bannerPlacement)
              .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

            // Only non-expired banners appear in the position strip and are draggable
            const stripGroup = group.filter(b => !(b.endDate && new Date(b.endDate) < now));

            if (group.length === 0 && !showBannerForm) {
              return (
                <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl">
                  <Megaphone size={36} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm font-medium">Aucune bannière pour {PLACEMENT_META[bannerPlacement].label}</p>
                </div>
              );
            }

            return (
              <div>
                {/* Visual position strip — drag-and-drop to reorder */}
                {stripGroup.length > 0 && (
                  <div className="mb-5">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Aperçu des positions · <span className="font-normal normal-case text-blue-400">Glisser pour réordonner</span>
                    </p>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {stripGroup.map((banner, idx) => {
                        const imgSrc = getBannerImageUrl(banner.imageUrl);
                        const isDragging = dragId === banner.id;
                        const isDragOver = dragOverId === banner.id && dragId !== banner.id;
                        return (
                          <div
                            key={banner.id}
                            draggable
                            onDragStart={e => { setDragId(banner.id); e.dataTransfer.effectAllowed = 'move'; }}
                            onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverId(banner.id); }}
                            onDrop={async e => {
                              e.preventDefault();
                              const fromId = dragId;
                              setDragId(null); setDragOverId(null);
                              if (!fromId || fromId === banner.id) return;
                              const fromIdx = stripGroup.findIndex(b => b.id === fromId);
                              const toIdx   = stripGroup.findIndex(b => b.id === banner.id);
                              if (fromIdx === -1 || toIdx === -1) return;
                              const reordered = [...stripGroup];
                              const [moved] = reordered.splice(fromIdx, 1);
                              reordered.splice(toIdx, 0, moved);
                              const items = reordered.map((b, i) => ({ id: b.id, position: i }));
                              try { await reorderBanners({ variables: { items } }); await refetchBanners(); toast.success('Ordre mis à jour'); }
                              catch { toast.error('Erreur lors du réordonnancement'); }
                            }}
                            onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                            className={`flex-shrink-0 w-32 flex flex-col items-center gap-1 cursor-grab select-none transition-all ${isDragging ? 'opacity-40 scale-95' : ''} ${isDragOver ? 'ring-2 ring-blue-400 rounded-lg' : ''}`}
                          >
                            <div className={`relative w-full h-16 rounded-lg border-2 overflow-hidden ${banner.isLive ? 'border-green-400' : 'border-gray-300'}`}>
                              {imgSrc
                                ? <img src={imgSrc} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                                : <div className="w-full h-full bg-gray-100 flex items-center justify-center"><Megaphone size={18} className="text-gray-300" /></div>
                              }
                            </div>
                            <span className="text-xs text-gray-500 font-semibold">Pos. {idx}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Banner list */}
                <div className="space-y-2">
                  {group.map(banner => {
                    const expired = banner.endDate && new Date(banner.endDate) < now;
                    return (
                      <div key={banner.id}>
                        {editingBanner?.id === banner.id ? (
                          <BannerForm initial={banner} rates={rates} allProducts={allBannerProducts} onSave={handleUpdateBanner} onCancel={() => setEditingBanner(null)} />
                        ) : (
                          <div className={`flex items-center gap-4 bg-white rounded-xl px-4 py-3 border transition-all ${expired ? 'border-red-200 opacity-70' : banner.isLive ? 'border-green-200' : 'border-gray-100 opacity-60'}`}>
                            {/* Position badge */}
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black shrink-0 ${expired ? 'bg-red-100 text-red-600' : banner.isLive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                              {banner.position ?? '—'}
                            </div>
                            <img src={getBannerImageUrl(banner.imageUrl)} alt="" className="w-24 h-12 object-cover rounded-lg border border-gray-100 shrink-0"
                              onError={e => { e.target.className = 'w-24 h-12 rounded-lg bg-gray-100 shrink-0'; }} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                {expired
                                  ? <span className="text-xs font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full flex items-center gap-1"><AlertCircle size={9} /> Expiré</span>
                                  : banner.isLive
                                    ? <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full flex items-center gap-1"><Eye size={9} /> En ligne</span>
                                    : <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full flex items-center gap-1"><EyeOff size={9} /> Hors ligne</span>
                                }
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                <span className={`flex items-center gap-1 ${expired ? 'text-red-500 font-semibold' : ''}`}>
                                  <CalendarDays size={10} />{fmtDate(banner.startDate)} → {fmtDate(banner.endDate)}
                                </span>
                                <span className="flex items-center gap-1"><DollarSign size={10} />{fmt(banner.pricePerDay)} DH/j</span>
                                <span className="font-semibold text-green-700">{fmt(banner.totalCost)} DH total</span>
                              </div>
                              {banner.promotion && <p className="text-xs text-green-600 truncate mt-0.5 font-semibold">→ {banner.promotion.name}</p>}
                              {!banner.promotion && banner.linkUrl && <p className="text-xs text-blue-400 truncate mt-0.5">→ {banner.linkUrl}</p>}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {!expired && (
                                <button onClick={() => handleToggleBanner(banner)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700">
                                  {banner.isActive ? <ToggleRight size={18} className="text-green-600" /> : <ToggleLeft size={18} />}
                                </button>
                              )}
                              <button onClick={() => setEditingBanner(banner)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700"><Edit2 size={14} /></button>
                              <button onClick={() => handleRemoveBanner(banner.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      )}
      {/* ── Calendar tab ── */}
      {tab === 'calendar' && (() => {
        const today = new Date();
        const totalDays = calDaysInMonth(calYear, calMonth);
        const offset    = calFirstOffset(calYear, calMonth);
        const cells     = [...Array(offset).fill(null), ...Array.from({ length: totalDays }, (_, i) => i + 1)];
        const monthLabel = new Date(calYear, calMonth, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

        const prevMonth = () => { if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); } else setCalMonth(m => m - 1); setCalDay(null); };
        const nextMonth = () => { if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); } else setCalMonth(m => m + 1); setCalDay(null); };

        return (
          <div>
            {/* Legend */}
            <div className="flex items-center gap-4 mb-4 flex-wrap">
              {[
                { dot: '#F59E0B', label: 'Produit sponsorisé' },
                { dot: '#3B82F6', label: "Bannière Accueil" },
                { dot: '#8B5CF6', label: 'Bannière Scanner' },
              ].map(({ dot, label }) => (
                <div key={label} className="flex items-center gap-1.5">
                  <div style={{ backgroundColor: dot }} className="w-3 h-3 rounded-full shrink-0" />
                  <span className="text-xs text-gray-500">{label}</span>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              {/* Month navigation */}
              <div className="flex items-center justify-between mb-4">
                <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
                  <CalendarDays size={16} className="rotate-180" />
                </button>
                <h3 className="text-sm font-bold text-gray-800 capitalize">{monthLabel}</h3>
                <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
                  <CalendarDays size={16} />
                </button>
              </div>

              {/* Day-of-week headers */}
              <div className="grid grid-cols-7 mb-1">
                {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(d => (
                  <div key={d} className="text-center text-xs font-bold text-gray-400 pb-2">{d}</div>
                ))}
              </div>

              {/* Day cells */}
              <div className="grid grid-cols-7 gap-0.5">
                {cells.map((d, i) => {
                  if (!d) return <div key={`e-${i}`} className="h-14" />;
                  const dots = getDotsForDay(d);
                  const isToday = today.getFullYear() === calYear && today.getMonth() === calMonth && today.getDate() === d;
                  const isSelected = calDay === d;
                  return (
                    <button
                      key={d}
                      onClick={() => setCalDay(d === calDay ? null : d)}
                      className={`h-14 flex flex-col items-center justify-start pt-1.5 rounded-xl transition-all ${isSelected ? 'bg-green-700 text-white shadow-sm' : isToday ? 'bg-green-50 text-green-800 font-bold' : 'hover:bg-gray-50 text-gray-700'}`}
                    >
                      <span className="text-xs font-semibold leading-none">{d}</span>
                      {dots.length > 0 && (
                        <div className="flex flex-wrap justify-center gap-0.5 mt-1 px-1">
                          {dots.slice(0, 4).map(ev => (
                            <div key={ev.id} style={{ backgroundColor: isSelected ? '#fff' : ev.dotColor }} className="w-1.5 h-1.5 rounded-full" />
                          ))}
                          {dots.length > 4 && <span className={`text-[9px] leading-none ${isSelected ? 'text-white' : 'text-gray-400'}`}>+</span>}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Day detail panel */}
            {calDay && (
              <div className="mt-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                  {new Date(calYear, calMonth, calDay).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  {' · '}
                  <span className={calDayEvents.length > 0 ? 'text-green-700' : 'text-gray-400'}>
                    {calDayEvents.length} événement{calDayEvents.length !== 1 ? 's' : ''}
                  </span>
                </p>
                {calDayEvents.length === 0 ? (
                  <p className="text-sm text-gray-400 italic text-center py-4">Aucune publicité active ce jour.</p>
                ) : (
                  <div className="space-y-2">
                    {calDayEvents.map(ev => {
                      const Icon = ev.Icon;
                      return (
                        <div key={ev.id} className={`flex items-start gap-3 p-3 rounded-xl border ${ev.badgeCls}`}>
                          <div style={{ color: ev.dotColor }} className="mt-0.5 shrink-0">
                            <Icon size={16} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{ev.label}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {ev.sub && <span>{ev.sub} · </span>}
                              {ev.from.toLocaleDateString('fr-FR')} → {ev.to.toLocaleDateString('fr-FR')}
                            </p>
                            {ev.extra && <p className="text-xs font-semibold mt-0.5" style={{ color: ev.dotColor }}>{ev.extra}</p>}
                          </div>
                          <span className={`text-xs font-bold px-2 py-1 rounded-lg border ${ev.badgeCls}`}>
                            {ev.type === 'sponsored' ? 'Sponsorisé' : 'Bannière'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default Advertising;
